/**
 * /advertisers/widgets — advertiser dashboard widgets (feature #152).
 *
 * Declares the TanStack Router route and delegates rendering to
 * `AdvertiserWidgetsPage`. The flat-file naming convention uses dots as path
 * separators, so `advertisers.widgets.tsx` maps to `/advertisers/widgets`.
 *
 * Same shape as `advertisers.portal.tsx`: a `validateSearch` that accepts an
 * optional `?advertiserId=` deep-link so operators can jump straight to an
 * advertiser's layout.
 */

import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { AdvertiserWidgetsPage } from "@/features/advertiser-widgets/AdvertiserWidgetsPage";

// ---------------------------------------------------------------------------
// Route — mounts at `/advertisers/widgets`.
// ---------------------------------------------------------------------------

interface AdvertiserWidgetsSearch {
	advertiserId?: string;
}

export const advertisersWidgetsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/advertisers/widgets",
	validateSearch: (raw: Record<string, unknown>): AdvertiserWidgetsSearch => {
		const advertiserId = raw.advertiserId;
		if (typeof advertiserId === "string" && advertiserId.trim().length > 0) {
			return { advertiserId: advertiserId.trim() };
		}
		return {};
	},
	component: AdvertiserWidgetsRoutePage,
});

export default advertisersWidgetsRoute;

// Wrapper reads the search param so the page can deep-link into a specific
// advertiser's layout without prop drilling from the router call site.
export function AdvertiserWidgetsRoutePage() {
	// Search param read for future deep-link wiring; AdvertiserWidgetsPage
	// does not currently accept an `initialAdvertiserId` prop.
	void advertisersWidgetsRoute.useSearch();
	return <AdvertiserWidgetsPage />;
}

// Re-export for the wire-up cycle / root registry import.
export { AdvertiserWidgetsPage } from "@/features/advertiser-widgets/AdvertiserWidgetsPage";
